/**
 * CrazyGamesService — CrazyGames SDK (v3) integration for the /crazygames
 * subpage.
 *
 * CrazyGames embeds the game in an iframe at <origin>/crazygames. Players
 * there never see the login screen: their identity comes from the
 * CrazyGames platform. The flow (per CrazyGames' docs):
 *
 *   const token = await window.CrazyGames.SDK.user.getUserToken();
 *
 *   → POST /api/auth/crazygames { token }
 *
 * The token is a server-verifiable JWT (RS256, signed by CrazyGames)
 * carrying { userId, gameId, username, exp }. The BACKEND verifies it and
 * maps it to a real user account (creating one on first sight) — so
 * CrazyGames players are fully logged-in users (skins, stats,
 * achievements, toplist, streak). The client never authenticates on its
 * own: `__dangerousUserId` (client-side, spoofable) is deliberately NOT
 * sent anywhere.
 *
 * This service only does the SDK side:
 *  - detect the CrazyGames context (URL path — the same index.html serves
 *    both / and /crazygames)
 *  - load the SDK script exactly once (CDN)
 *  - `await CrazyGames.SDK.init()` (required before touching `user`)
 *  - get the logged-in user, prompting the platform's auth prompt when
 *    needed, then return a fresh user token (the token expires in ~1 h
 *    and the SDK refreshes it — it is requested on demand, never stored)
 *
 * Testability: a fake SDK can be injected (the `sdk` option or a pre-set
 * window.CrazyGames), so tests never load the real script.
 */

// ─── Context detection ────────────────────────────────────────────────────

/**
 * The CrazyGames subpage lives at /crazygames (exactly, or with a trailing
 * segment). Pure over the PATHNAME so it is trivially testable.
 * /crazygames2, /other/crazygames etc. are NOT the subpage.
 */
export function isCrazyGamesContext(pathname: string): boolean {
  return pathname === '/crazygames' || pathname.startsWith('/crazygames/');
}

// ─── SDK surface (the parts of window.CrazyGames.SDK we use) ──────────────

/** A user as reported by the CrazyGames SDK (v3 `user.getUser()`). */
export interface CrazyGamesUser {
  /**
   * The raw platform user id. Client-side information ONLY (CrazyGames
   * explicitly warns it can be spoofed) — never send it to our backend;
   * the verified token is the credential.
   */
  __dangerousUserId?: string;
  username: string;
  profilePictureUrl?: string;
}

export interface CrazyGamesUserModule {
  /** True when the platform offers user accounts for this game. */
  readonly isUserAccountAvailable: boolean;
  /** The signed-in platform user, or null when not signed in. */
  getUser(): Promise<CrazyGamesUser | null>;
  /**
   * Show the platform's sign-in prompt (only when not signed in).
   * Resolves with the user once they sign in.
   */
  showAuthPrompt(): Promise<CrazyGamesUser | null>;
  /**
   * A fresh RS256 user token (JWT, ~1 h lifetime, SDK auto-refreshes it).
   * Request it when needed — never store it.
   */
  getUserToken(): Promise<string>;
  /**
   * Register a listener for platform AUTH events — e.g. a guest signing
   * in on CrazyGames WHILE the game is running. CrazyGames expects the
   * game to switch to the account identity (fresh token → server).
   */
  addAuthListener(listener: (user: CrazyGamesUser) => void): void;
  /** Remove a previously registered auth listener. */
  removeAuthListener(listener: (user: CrazyGamesUser) => void): void;
}

export interface CrazyGamesSdk {
  /** Required before any `user` access. */
  init(): Promise<unknown>;
  user: CrazyGamesUserModule;
  /**
   * TOP-LEVEL SDK property (NOT on the `game` module): the runtime the
   * SDK detected — 'crazygames' on the platform, 'disabled'/undefined
   * off-platform. The SDK logs it in its init response.
   */
  environment?: string;
}

/** Read the global SDK object (present once the script has loaded). */
export function getGlobalCrazyGames(): CrazyGamesSdk | null {
  const g = globalThis as { CrazyGames?: { SDK?: unknown } };
  return (g.CrazyGames?.SDK ?? null) as CrazyGamesSdk | null;
}

// ─── Script loading ───────────────────────────────────────────────────────

/** The CrazyGames SDK v3 script (served by CrazyGames' CDN). */
export const CRAZYGAMES_SDK_URL = 'https://sdk.crazygames.com/crazygames-sdk-v3.js';

let _scriptLoad: Promise<void> | null = null;

/**
 * Load the CrazyGames SDK script exactly once (module-level dedupe — the
 * app may probe the SDK across shell restarts).
 */
export function loadCrazyGamesSdkScript(doc?: Document): Promise<void> {
  if (getGlobalCrazyGames()) return Promise.resolve();
  if (_scriptLoad) return _scriptLoad;
  const d = doc ?? document;
  _scriptLoad = new Promise<void>((resolve, reject) => {
    // NOTE: no "a script tag exists" guard here — a tag from a FAILED load
    // stays in the DOM, and resolving on it would report "loaded" while
    // window.CrazyGames was never defined. A failed load clears _scriptLoad,
    // so the next call injects a fresh tag (a real re-download).
    const script = d.createElement('script');
    script.src = CRAZYGAMES_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      _scriptLoad = null; // allow a retry after a failed load
      reject(new Error('Failed to load the CrazyGames SDK.'));
    };
    d.head.appendChild(script);
  });
  return _scriptLoad;
}

/** Test hook: drop the script-load promise (lets tests exercise a fresh load). */
export function resetCrazyGamesSdkScriptLoad(): void {
  _scriptLoad = null;
}

// ─── Service ──────────────────────────────────────────────────────────────

export interface CrazyGamesServiceOptions {
  /**
   * A pre-existing SDK instance (tests). When provided, the script is
   * never loaded and this object is used.
   */
  sdk?: CrazyGamesSdk;
  /**
   * Total budget for the whole sign-in (init + login prompt + token).
   * The prompt is interactive, so this is generous. Default: 120 s.
   */
  timeoutMs?: number;
  /** setTimeout/clearTimeout overrides (tests use fake timers). */
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
}

export interface CrazyGamesLoginResult {
  /** The fresh RS256 user token to POST to /api/auth/crazygames. */
  token: string;
  /** The platform display username (informational — the account key the
   *  server derives from the VERIFIED token is authoritative). */
  username: string;
}

export class CrazyGamesService {
  private readonly _opts: CrazyGamesServiceOptions;
  /** SDK init (script load + `init()`) runs ONCE per service instance. */
  private _userModule: Promise<CrazyGamesUserModule> | null = null;

  constructor(opts: CrazyGamesServiceOptions = {}) {
    this._opts = opts;
  }

  /**
   * The signed-in platform user WITHOUT prompting — null when the visitor
   * is not signed in on the platform (the caller then decides: auto-login
   * is impossible, so it offers its own choice, e.g. guest + "Sign in to
   * save progress"). Rejects when the SDK cannot load or accounts are
   * unavailable for this game (off-platform).
   */
  async getUser(): Promise<CrazyGamesUser | null> {
    const userModule = await this.ensureUserModule();
    return userModule.getUser();
  }

  /**
   * A fresh user token for an ALREADY-signed-in user (1 h lifetime,
   * SDK-refreshed — request on demand, never store).
   */
  async getUserToken(): Promise<string> {
    const userModule = await this.ensureUserModule();
    const token = await userModule.getUserToken();
    if (typeof token !== 'string' || token.length === 0) {
      throw new Error('The CrazyGames SDK returned an empty token.');
    }
    return token;
  }

  /**
   * The platform's OFFICIAL sign-in / register popup (the QA "Login with
   * CrazyGames" button). Resolves with the user once they sign in.
   */
  async showAuthPrompt(): Promise<CrazyGamesUser | null> {
    const userModule = await this.ensureUserModule();
    return userModule.showAuthPrompt();
  }

  /**
   * Register a listener for platform AUTH events (a guest logging in on
   * CrazyGames while BattleBrew is running — the flow CrazyGames
   * specifically expects). Returns a cleanup function that removes the
   * listener. Resolves to a no-op cleanup when the SDK is unavailable.
   */
  async addAuthListener(handler: (user: CrazyGamesUser) => void): Promise<() => void> {
    // The auth listener is only meaningful ON-PLATFORM (an SDK `user`
    // module to listen on). If the global is absent there is no platform —
    // return a no-op cleanup immediately (never attempt a script load here,
    // which would hang off-platform).
    if (!getGlobalCrazyGames()) {
      return () => {
        /* no-op — no platform SDK */
      };
    }
    try {
      const userModule = await this.ensureUserModule();
      userModule.addAuthListener(handler);
      return () => {
        try {
          userModule.removeAuthListener(handler);
        } catch {
          /* best-effort removal */
        }
      };
    } catch {
      return () => {
        /* no-op — SDK unavailable */
      };
    }
  }

  /**
   * Sign the visitor in through the CrazyGames platform and obtain a fresh
   * user token: prompts the platform's own account popup when not signed
   * in. Resolves with `{ token, username }`; rejects when the SDK cannot
   * load, accounts are unavailable, the prompt is cancelled, or nothing
   * completes within `timeoutMs`.
   */
  async getLoginToken(): Promise<CrazyGamesLoginResult> {
    const setTimer = this._opts.setTimeoutFn ?? setTimeout;
    const clearTimer = this._opts.clearTimeoutFn ?? clearTimeout;
    const timeoutMs = this._opts.timeoutMs ?? 120_000;

    const work = (async (): Promise<CrazyGamesLoginResult> => {
      const userModule = await this.ensureUserModule();
      let user = await userModule.getUser();
      if (!user) {
        user = await userModule.showAuthPrompt(); // platform sign-in prompt
      }
      if (!user) {
        throw new Error('The CrazyGames sign-in was cancelled.');
      }
      const token = await userModule.getUserToken();
      if (typeof token !== 'string' || token.length === 0) {
        throw new Error('The CrazyGames SDK returned an empty token.');
      }
      return { token, username: user.username };
    })();

    // Interactive prompt → generous overall budget.
    return new Promise<CrazyGamesLoginResult>((resolve, reject) => {
      const timer = setTimer(() => {
        reject(new Error('The CrazyGames sign-in did not complete in time.'));
      }, timeoutMs);
      work.then(
        (result) => {
          clearTimer(timer);
          resolve(result);
        },
        (err) => {
          clearTimer(timer);
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      );
    });
  }

  // ─── Internal ───────────────────────────────────────────────────────────

  /**
   * Ensure the SDK script is loaded and `init()` has run — required
   * BEFORE touching the `game` module (multiplayer rooms). Rejects when
   * the SDK is unavailable (off-platform / blocked). Safe to call
   * multiple times (the SDK tolerates repeated init).
   */
  async ensureSdkInitialized(): Promise<void> {
    let sdk = getGlobalCrazyGames();
    if (!sdk) {
      await loadCrazyGamesSdkScript();
      sdk = getGlobalCrazyGames();
    }
    if (!sdk) throw new Error('The CrazyGames SDK is not available.');
    await sdk.init();
  }

  private ensureUserModule(): Promise<CrazyGamesUserModule> {
    if (this._userModule) return this._userModule;
    this._userModule = (async () => {
      let sdk = this._opts.sdk ?? getGlobalCrazyGames();
      if (!sdk) {
        await loadCrazyGamesSdkScript();
        sdk = getGlobalCrazyGames();
      }
      if (!sdk) throw new Error('The CrazyGames SDK is not available.');

      await sdk.init(); // required before the user module

      const userModule = sdk.user;
      if (!userModule || !userModule.isUserAccountAvailable) {
        throw new Error('CrazyGames accounts are not available for this game.');
      }
      return userModule;
    })();
    return this._userModule;
  }
}
